/**
 * WebAssembly (Wasm) Sandbox Loader
 * 
 * This module instantiates the memory-safe WebAssembly binary.
 * All critical input sanitization runs strictly inside the Wasm VM.
 */

let wasmInstance = null;
let wasmMemory = null;

export async function initWasm() {
  if (wasmInstance) return;

  try {
    // In Vite, assets in the public folder are served at the root
    const response = await fetch('/sanitizer.wasm');
    const buffer = await response.arrayBuffer();

    const module = await WebAssembly.instantiate(buffer, {
      env: {
        abort: () => console.error("Wasm aborted")
      }
    });

    wasmInstance = module.instance.exports;
    wasmMemory = wasmInstance.memory;
    console.log("🛡️ WebAssembly Sandbox Initialized");
  } catch (error) {
    console.error("Failed to load Wasm sandbox:", error);
  }
}

/**
 * Passes a string from the JavaScript V8 engine into the WebAssembly sandbox memory
 * and executes the compiled validation logic.
 * 
 * @param {string} text The input to validate
 * @returns {boolean} True if safe, False if malicious payload detected
 */
export function validateInputWasm(text) {
  if (!wasmInstance) {
    // Fallback if Wasm hasn't loaded yet
    console.warn('Wasm not ready, falling back to JS regex');
    return !/[<>{}$]/.test(text);
  }

  // Convert JS string to UTF-8 bytes
  const encoder = new TextEncoder();
  const bytes = encoder.encode(text);
  
  // Create a view into the Wasm memory
  const memoryView = new Uint8Array(wasmMemory.buffer);
  
  // We use memory offset 0 for simplicity (assuming single threaded, no allocations needed)
  // For production with concurrent allocations, we would use AssemblyScript's memory manager.
  const pointer = 0; 
  
  // Copy the bytes into the Wasm Sandbox
  memoryView.set(bytes, pointer);

  // Execute the memory-safe compiled Rust/AssemblyScript validation
  const isSafe = wasmInstance.validateInput(pointer, bytes.length);
  
  return isSafe === 1;
}
